// src/pages/auth/LoginPage.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  ArrowLeft, 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  ArrowRight,
  Search,
  Wrench,
  AlertCircle,
  Loader2,
  Briefcase,
  User,
  Shield
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useTranslation from '../../hooks/useTranslation';
import colors from '../../constants/colors';
import styles from '../../styles/LoginPage.module.css';

// ============================================================
// REDUX IMPORTS
// ============================================================

import { login, resetLoginState, clearAuthErrors } from '../../store/slices/authSlice';
import { selectIsAuthenticated, selectLoggingIn, selectLoginError, selectLoginSuccess } from '../../store/slices/authSlice';

const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  // ============================================================
  // REDUX SELECTORS
  // ============================================================

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isLoading = useSelector(selectLoggingIn);
  const loginError = useSelector(selectLoginError);
  const loginSuccess = useSelector(selectLoginSuccess);

  // ============================================================
  // CONTEXT HOOKS
  // ============================================================

  const { 
    currentLanguage, 
    detectUserLocation, 
    isDetectingLocation,
    locationDetected,
    detectedCountry 
  } = useLanguage();

  // ============================================================
  // TRANSLATIONS
  // ============================================================

  const { translatedText: errorTitle } = useTranslation('Error');
  const { translatedText: fillAllFields } = useTranslation('Please fill in all fields');
  const { translatedText: validEmail } = useTranslation('Please enter a valid email address');
  const { translatedText: loginFailed } = useTranslation('Login Failed');
  const { translatedText: invalidCredentials } = useTranslation('Invalid credentials');
  
  const { translatedText: appName } = useTranslation('Haitian Services Platform');
  const { translatedText: providerPortalSubtitle } = useTranslation('Sign in to manage your services');
  const { translatedText: providerLogin } = useTranslation('Service Provider Login');
  
  const { translatedText: emailLabel } = useTranslation('Email Address');
  const { translatedText: emailPlaceholder } = useTranslation('Enter your registered email');
  const { translatedText: passwordLabel } = useTranslation('Password');
  const { translatedText: passwordPlaceholder } = useTranslation('Enter your password');
  const { translatedText: forgotPassword } = useTranslation('Forgot Password?');
  const { translatedText: signInProvider } = useTranslation('Sign In as Provider');
  
  const { translatedText: lookingForServices } = useTranslation('Looking for services?');
  const { translatedText: clientSubtext } = useTranslation('Clients can browse and find trusted providers without an account');
  const { translatedText: browseServices } = useTranslation('Browse Services as Client');
  
  const { translatedText: newProvider } = useTranslation('New service provider?');
  const { translatedText: registerHere } = useTranslation('Register Here');
  const { translatedText: adminPortal } = useTranslation('Admin Portal');
  const { translatedText: goToAdmin } = useTranslation('Go to Admin Login');

  // ============================================================
  // DETECT LOCATION ON MOUNT
  // ============================================================

  useEffect(() => {
    const detectLocation = async () => {
      try {
        console.log('📍 LoginPage: Detecting location...');
        await detectUserLocation();
        console.log('📍 LoginPage: Location detection complete');
        console.log('📍 Current language:', currentLanguage);
        console.log('📍 Detected country:', detectedCountry);
      } catch (error) {
        console.log('Location detection error:', error);
      }
    };
    
    detectLocation();
  }, []);

  // ============================================================
  // REDIRECT IF ALREADY AUTHENTICATED
  // ============================================================

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/provider-dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // ============================================================
  // HANDLE LOGIN SUCCESS
  // ============================================================

  useEffect(() => {
    if (loginSuccess) {
      dispatch(resetLoginState());
      navigate('/provider-dashboard', { replace: true });
    }
  }, [loginSuccess, dispatch, navigate]);

  // ============================================================
  // HANDLE LOGIN ERROR
  // ============================================================

  useEffect(() => {
    if (loginError) {
      setError(loginError);
      // Clear error after 5 seconds
      const timer = setTimeout(() => {
        dispatch(clearAuthErrors());
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [loginError, dispatch]);

  // ============================================================
  // HANDLE LOGIN
  // ============================================================

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    // Validate fields
    if (!email || !password) {
      setError(fillAllFields);
      return;
    }

    if (!email.includes('@')) {
      setError(validEmail);
      return;
    }

    // Dispatch login action
    dispatch(login({ email, password, role: 'service_provider' }));
  };

  // ============================================================
  // HANDLE BROWSE AS CLIENT
  // ============================================================

  const handleBrowseAsClient = () => {
    navigate('/home', { replace: true });
  };

  // ============================================================
  // TOGGLE PASSWORD VISIBILITY
  // ============================================================

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginWrapper}>
        <div className={styles.loginCard}>
          {/* Back Button */}
          <button 
            className={styles.backButton}
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={24} color={colors.textPrimary} />
          </button>

          {/* Logo Section */}
          <div className={styles.logoSection}>
            <div className={styles.logoContainer}>
              <div className={styles.logoBox}>
                <img 
                  src="/logo/logo_hsm.png" 
                  alt="Haitian Services Marketplace" 
                  className={styles.logoImage}
                />
              </div>
              <h1 className={styles.logoText}>{appName}</h1>
            </div>
            <p className={styles.subtitle}>{providerPortalSubtitle}</p>
          </div>

          {/* Provider Badge */}
          <div className={styles.providerBadge} style={{ borderColor: colors.primary }}>
            <Wrench size={20} />
            <span className={styles.providerBadgeText} style={{ color: colors.primary }}>
              {providerLogin}
            </span>
          </div>

          {/* Admin Link */}
          <div className={styles.adminLinkContainer}>
            <Link to="/admin/login" className={styles.adminLink}>
              <Shield size={16} />
              <span>{goToAdmin}</span>
              <ArrowRight size={14} />
            </Link>
          </div>

          {/* Error Message */}
          {error && (
            <div className={styles.errorMessage}>
              <AlertCircle size={18} />
              <span className={styles.errorText}>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleLogin}>
            <div className={styles.inputGroup}>
              <label className={styles.formLabel}>{emailLabel}</label>
              <div className={styles.inputWrapper}>
                <Mail size={20} className={styles.inputIcon} color={colors.gray400} />
                <input
                  type="email"
                  className={styles.formControl}
                  placeholder={emailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoCapitalize="none"
                  autoComplete="email"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.formLabel}>{passwordLabel}</label>
              <div className={styles.inputWrapper}>
                <Lock size={20} className={styles.inputIcon} color={colors.gray400} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  className={`${styles.formControl} ${styles.formControlWithEye}`}
                  placeholder={passwordPlaceholder}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  disabled={isLoading}
                />
                <button 
                  type="button"
                  className={styles.eyeButton}
                  onClick={togglePasswordVisibility}
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeOff size={20} color={colors.gray400} />
                  ) : (
                    <Eye size={20} color={colors.gray400} />
                  )}
                </button>
              </div>
            </div>

            <div className={styles.forgotPasswordContainer}>
              <button type="button" className={styles.forgotPassword}>
                {forgotPassword}
              </button>
            </div>

            <button 
              type="submit"
              className={styles.loginButton}
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 size={20} className={styles.spinner} />
              ) : (
                <>
                  <span>{signInProvider}</span>
                  <ArrowRight size={20} />
                </>
              )}
            </button>

            {/* Info for Clients */}
            <div className={styles.clientInfoContainer}>
              <div className={styles.clientInfoDivider}>
                <div className={styles.dividerLine} />
                <span className={styles.clientInfoText}>{lookingForServices}</span>
                <div className={styles.dividerLine} />
              </div>
              <p className={styles.clientInfoSubtext}>
                {clientSubtext}
              </p>
              <button 
                type="button"
                className={styles.browseButton}
                onClick={handleBrowseAsClient}
                disabled={isLoading}
              >
                <Search size={20} color={colors.primary} />
                <span className={styles.browseButtonText}>{browseServices}</span>
              </button>
            </div>
          </form>

          {/* Register Link for Providers */}
          <div className={styles.registerLinkContainer}>
            <span className={styles.registerText}>{newProvider}</span>
            <Link to="/register" className={styles.registerLink}>
              {registerHere}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;